/** @jsx jsx */
import { Col, Container } from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import useLink from '../../useLink';
import BrandBanner from '../Brand/BrandBanner';
import ContentRow from '../ContentRow';
import Layout from '../Layout';
import ProductActions from '../Product/ProductActions';
import ProductSections from '../ProductPage/ProductSections';
import RelatedProductsSection from '../ProductPage/RelatedProductsSection';
import Related from '../Related/Related';
import Section from '../Section/Section';
import SEO from '../seo';
import EventProductPageHeader from './EventProductPageHeader';

export default function EventProductPage({
  product,
  event,
  otherProducts,
  otherProductsName,
  relatedProducts,
  location,
  previous,
  next,
  locale,
}) {
  const link = useLink();
  const hasOtherProducts = Boolean(otherProducts && otherProducts.length);
  const hasRelatedProducts = Boolean(relatedProducts && relatedProducts.length);
  const hasRelated = hasOtherProducts || hasRelatedProducts;
  const { exhibitor, producer } = product || {};
  const standPath = exhibitor
    ? link.exhibitor(exhibitor)
    : link.producer(producer);

  const name = otherProductsName;
  const page = {
    title: `${product.fullName}`,
    path: location.href,
    image: product.bottleImage?.fixed?.src,
    description: product.description,
  };
  return (
    <Layout locale={locale} sx={{ backgroundColor: 'transparent' }}>
      <SEO page={page} />
      {exhibitor ? (
        <BrandBanner {...exhibitor} />
      ) : (
        <BrandBanner {...producer} />
      )}
      <Container>
        <EventProductPageHeader
          product={product}
          overlapImage
          previous={previous}
          next={next}
          standPath={standPath}
        />
      </Container>

      <Container>
        <ContentRow>
          <Col sx={{ paddingY: 3 }} flex>
            <ProductActions product={product} />
          </Col>
        </ContentRow>
      </Container>

      <ProductSections
        exhibitor={exhibitor}
        product={product}
        eventId={event.eventId}
      />

      {hasRelated && (
        <Section
          sx={{ backgroundColor: 'light', paddingTop: 4, paddingBottom: 5 }}
        >
          {hasOtherProducts && (
            <RelatedProductsSection
              registeredProducts={otherProducts}
              exhibitor={exhibitor}
              title={
                name ? (
                  <Trans>More from {name}</Trans>
                ) : (
                  <Trans>Other products</Trans>
                )
              }
              link={
                <Related.Link to={standPath}>
                  <Trans>See profile →</Trans>
                </Related.Link>
              }
              sx={{ paddingY: 4 }}
            />
          )}

          {hasRelatedProducts && (
            <RelatedProductsSection
              registeredProducts={relatedProducts}
              exhibitor={exhibitor}
              title={<Trans>Related products</Trans>}
              link={
                <Related.Link to={standPath}>
                  <Trans>See all</Trans>
                </Related.Link>
              }
              sx={{ paddingY: 4 }}
            />
          )}
        </Section>
      )}
    </Layout>
  );
}

export const fragment = graphql`
  fragment EventProductPage_RegisteredProduct on Bottlebooks_RegisteredProduct {
    ...ProductSections_RegisteredProduct
    ...bb_ProductActions_RegisteredProduct
    ...bb_EventProductPageHeaderRegisteredProduct
    producer {
      products {
        nodes {
          ...bb_RelatedProductsSection
        }
      }
    }
  }

  fragment bb_EventProductPage on Bottlebooks_Product {
    ...bb_ProductSections
    ...bb_ProductActions
    ...bb_Product_fullName
    ...bb_EventProductPageHeader
  }

  fragment EventProductPage_Related on Product {
    slug
    ...RelatedProductsSection
  }

  fragment EventProductPage on Product {
    exhibitor {
      slug
      name
      ...BrandBanner
      ...useLink_Exhibitor
    }
    producer {
      slug
      name
      ...BrandBanner
      ...useLink_Producer
    }
    ...EventProductPageHeader
    ...ProductSections
  }
`;
